/* MenuIntelligenceTab — Top sellers, dead items, Kasavana-Smith engineering
 * matrix, category performance, kitchens / peak hour / channels split.
 * All live from seeded POS data. */
import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { TrendingUp, AlertCircle, Star, Zap, HelpCircle, Dog, ChefHat, Clock, TrendingDown } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const QUADRANT_META = {
  star:      { label: 'Stars',      color: '#10B981', icon: Star,       hint: 'High volume + high value — push harder, protect the recipe' },
  plowhorse: { label: 'Plowhorses', color: '#3B82F6', icon: Zap,        hint: 'High volume + low value — raise price or cut cost' },
  puzzle:    { label: 'Puzzles',    color: '#F59E0B', icon: HelpCircle, hint: 'Low volume + high value — promote / feature' },
  dog:       { label: 'Dogs',       color: '#9CA3AF', icon: Dog,        hint: 'Low volume + low value — remove from menu' },
};

const MODE_LABEL = {
  margin: 'volume × margin (Kasavana-Smith)',
  price: 'volume × avg unit price',
  none: 'no data',
};

export default function MenuIntelligenceTab({ outletId, outlet }) {
  const [top, setTop] = useState(null);
  const [dead, setDead] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [cat, setCat] = useState(null);
  // Hunk 2 of Bundle 2.10 main: 3 new sections from /menu-intel/* —
  // kitchens revenue mix, hour-by-hour peak, channel split.
  const [kitchens, setKitchens] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [channels, setChannels] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, d, m, c, k, h, ch] = await Promise.all([
          api.get(`/outlet-intel/${outletId}/menu/top-sellers?days=7&limit=12`),
          api.get(`/outlet-intel/${outletId}/menu/dead-items?days=14`),
          api.get(`/outlet-intel/${outletId}/menu/engineering-matrix?days=14`),
          api.get(`/outlet-intel/${outletId}/menu/category-performance?days=7`),
          // Bundle 2.10 main · Hunk 2 — pull richer detail from the
          // canonical /menu-intel/* endpoints. Tolerate failure (the
          // tab still renders the legacy sections above).
          api.get(`/menu-intel/${outletId}/kitchens?range=14d`).catch(() => null),
          api.get(`/menu-intel/${outletId}/hour-by-hour?range=14d`).catch(() => null),
          api.get(`/menu-intel/${outletId}/channels?range=14d`).catch(() => null),
        ]);
        if (cancelled) return;
        setTop(t.data); setDead(d.data); setMatrix(m.data); setCat(c.data);
        setKitchens(k?.data || null);
        setHourly(h?.data || null);
        setChannels(ch?.data || null);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [outletId]);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading menu intelligence…</div>;

  return (
    <div className="space-y-4" data-testid="menu-intel">
      {/* Engineering matrix counts */}
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Menu Engineering Matrix</h3>
        <span
          className="text-[10px] tracking-wide px-2 py-1 rounded"
          style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}
          title={matrix?.mode === 'price'
            ? 'Per-dish recipe cost not yet available — using avg unit price as the value axis. The Kasavana-Smith volume × margin axis activates automatically once GRN/recipe data is wired.'
            : 'Volume × margin axis (canonical Kasavana-Smith).'}
          data-testid="matrix-mode-pill"
        >
          axis: {MODE_LABEL[matrix?.mode] || '—'}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(QUADRANT_META).map(([k, meta]) => {
          const Icon = meta.icon;
          const count = matrix?.counts?.[k] || 0;
          return (
            <div key={k} className="rounded-xl bg-white p-4" style={{ border: `1px solid ${meta.color}33`, borderLeft: `4px solid ${meta.color}` }}
              data-testid={`quadrant-${k}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={15} color={meta.color} />
                <span className="text-[12px] font-semibold" style={{ color: meta.color }}>{meta.label}</span>
              </div>
              <p className="text-2xl font-semibold" style={{ color: '#1B2A4A' }}>{count}</p>
              <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>{meta.hint}</p>
            </div>
          );
        })}
      </div>

      {/* Quadrant drilldowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {['star', 'plowhorse', 'puzzle', 'dog'].map(k => (
          <Card key={k} title={`${QUADRANT_META[k].label} · ${matrix?.counts?.[k] || 0}`} testid={`card-${k}`}>
            {(matrix?.quadrants?.[k] || []).slice(0, 6).map(r => (
              <ItemRow key={r.name} r={r} accent={QUADRANT_META[k].color} mode={matrix?.mode} />
            ))}
            {(!matrix?.quadrants?.[k]?.length) && <Empty />}
          </Card>
        ))}
      </div>

      <Card title="Top Sellers (7d)" testid="card-top">
        {(top?.rows || []).map(r => (
          <div key={r.name} className="py-1.5 flex items-center gap-3 text-[12px]">
            <span className="flex-1 truncate">{r.name}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>{r.category}</span>
            <span className="w-14 text-right text-[11px]" style={{ color: '#6B7280' }}>{r.qty} sold</span>
            <span className="w-20 text-right font-semibold">{INR(r.revenue)}</span>
            <span className="w-14 text-right text-[11px]" style={{ color: r.margin_pct > 60 ? '#10B981' : r.margin_pct > 40 ? '#F59E0B' : '#DC2626' }}>
              {r.margin_pct}%
            </span>
          </div>
        ))}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title={`Dead Items (no sales in ${dead?.days || 14}d) · ${dead?.total_dead || 0}`} testid="card-dead">
          {(dead?.rows || []).length === 0 ? (
            <p className="text-[12px]" style={{ color: '#10B981' }}>✓ Every active menu item sold at least once — good coverage.</p>
          ) : (dead.rows.slice(0, 10).map(r => (
            <div key={r.name} className="py-1 flex justify-between text-[12px]">
              <span>{r.name}</span>
              <span className="text-[10px]" style={{ color: '#6B7280' }}>{r.category} · {INR(r.price)}</span>
            </div>
          )))}
        </Card>

        <Card title="Category Performance (7d)" testid="card-category-perf">
          {(cat?.rows || []).map(r => (
            <div key={`${r.category}-${r.sub_category}`} className="py-1.5 flex items-center gap-2 text-[12px]">
              <div className="flex-1 min-w-0">
                <div className="flex justify-between">
                  <span className="font-medium">{r.category} · {r.sub_category}</span>
                  <span>{INR(r.revenue)}</span>
                </div>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>{r.qty} items · margin {r.margin_pct}%</p>
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* ─── Bundle 2.10 main · Hunk 2 — richer detail from /menu-intel/* ─── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Kitchens · 14d" testid="card-kitchens">
          {(kitchens?.kitchens || []).length === 0 ? (
            <Empty />
          ) : (kitchens.kitchens.slice(0, 8).map(k => (
            <div key={k.kitchen} className="py-1 flex items-center gap-2 text-[12px]">
              <ChefHat size={11} style={{ color: '#6B7280' }} />
              <span className="flex-1 truncate">{k.kitchen}</span>
              <span className="w-16 text-right font-semibold">{INR(k.revenue)}</span>
              <span className="w-12 text-right text-[10px]" style={{ color: '#6B7280' }}>
                {(k.pct_of_revenue || 0).toFixed(0)}%
              </span>
            </div>
          )))}
        </Card>

        <Card title="Peak Hour · 14d" testid="card-peak-hour">
          {!hourly?.peak_hour ? <Empty /> : (() => {
            const peak = hourly.peak_hour;
            const series = hourly.series || [];
            const maxRev = Math.max(...series.map(s => s.revenue || 0), 1);
            return (
              <>
                <div className="mb-2 flex items-center gap-2 text-[12px]">
                  <Clock size={12} color="#10B981" />
                  <span>
                    Peak: <strong>{peak.hour}</strong> ({INR(peak.revenue)} · {peak.orders} orders)
                  </span>
                </div>
                <div className="space-y-0.5">
                  {series.slice(0, 12).map(s => (
                    <div key={s.hour} className="flex items-center gap-2 text-[10px]">
                      <span className="w-10" style={{ color: '#6B7280' }}>{s.hour}</span>
                      <div className="flex-1 h-3 bg-gray-100 rounded overflow-hidden">
                        <div
                          className="h-full"
                          style={{
                            width: `${((s.revenue || 0) / maxRev) * 100}%`,
                            backgroundColor: s.hour === peak.hour ? '#10B981' : '#9CA3AF',
                          }}
                        />
                      </div>
                      <span className="w-14 text-right" style={{ color: '#6B7280' }}>
                        {INR(s.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </Card>

        <Card title="Channel Mix · 14d" testid="card-channels">
          {(channels?.channels || []).length === 0 ? (
            <Empty />
          ) : (
            <>
              {channels.channels.map(c => (
                <div key={c.channel} className="py-1 flex items-center gap-2 text-[12px]">
                  <span className="flex-1 truncate">{c.channel}</span>
                  <span className="w-14 text-right font-semibold">{INR(c.revenue)}</span>
                  <span className="w-10 text-right text-[10px]" style={{ color: '#6B7280' }}>
                    {(c.pct || 0).toFixed(0)}%
                  </span>
                </div>
              ))}
              {(channels.payment_methods || []).length > 0 && (
                <div className="pt-2 mt-2" style={{ borderTop: '1px solid #E5E1DC' }}>
                  <p className="text-[10px] font-semibold mb-1" style={{ color: '#6B7280' }}>Payment Mix</p>
                  {channels.payment_methods.slice(0, 4).map(p => (
                    <div key={p.method} className="py-0.5 flex justify-between text-[11px]">
                      <span>{p.method}</span>
                      <span>{INR(p.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}

function ItemRow({ r, accent, mode = 'price' }) {
  // In price mode the rightmost cell shows avg unit price (₹); in
  // margin mode it shows margin %. Tooltip explains which one this is.
  const rightLabel = mode === 'margin'
    ? `${r.margin_pct}%`
    : INR(r.avg_unit_price || 0);
  const rightColor = mode === 'margin'
    ? (r.margin_pct > 60 ? '#10B981' : '#6B7280')
    : '#6B7280';
  const rightTitle = mode === 'margin' ? 'Margin %' : 'Avg unit price';
  return (
    <div className="py-1 flex items-center gap-2 text-[12px]">
      <span className="w-1 h-6 rounded" style={{ backgroundColor: accent }} />
      <span className="flex-1 truncate">{r.name}</span>
      <span className="w-10 text-right text-[10px]" style={{ color: '#6B7280' }}>{r.qty}</span>
      <span className="w-16 text-right font-semibold">{INR(r.revenue)}</span>
      <span className="w-12 text-right text-[10px]" style={{ color: rightColor }} title={rightTitle}>{rightLabel}</span>
    </div>
  );
}

function Empty() {
  return <p className="text-[11px] py-2" style={{ color: '#9CA3AF' }}>No items.</p>;
}
